/**
 * Valor ao vivo com tendência e faixa (ISA-101): cinza na faixa, cor + forma fora dela — Nexora Grid (US-402).
 *
 * Criado em: 16/10/2026 · Última modificação: 17/10/2026 · Versão: 1.2.0
 *
 * v1.1 (retorno de 17/10/2026): o gráfico do card traz a ÚLTIMA HORA DA BASE (não só o que chegou desde que a tela
 * abriu), no estilo do card "stat" do Grafana: valor em cima, área com gradiente ocupando a largura, mín e máx da hora.
 * A faixa de tensão segue a tensão nominal da ficha (`tensao_v`) — 220 V ou 380 V.
 * v1.2: suavização (média móvel de 3 pontos e faixa mínima de ±5% da média) contra o "dente de serra".
 */
import { useId } from "react";
import { chave } from "./useTempoReal";
import { correnteNominal, tensaoNominal, useCco } from "./contexto";
import { useHistoricoHora, type Serie } from "./historico";
import { FORMA, MEDIDAS, NOME_PRIORIDADE, fmtValor, nomeMedida, unidadeMedida, valorDe } from "./medidas";

/** Gráfico de área da última hora (estilo Grafana), com a escala das horas e o mín/máx no título. */
export function GraficoHora({ serie, medida, anormal }: { serie: Serie; medida: string; anormal?: boolean }) {
  const id = useId().replace(/:/g, "");
  const L = 300, A = 56;
  if (serie.length < 2) return <div className="grafico-hora vazio nx-suave">sem histórico na última hora</div>;
  const brutos = serie.map((p) => valorDe(medida, p.v));
  // média móvel centrada de 3 pontos: valores muito próximos não viram "dente de serra"
  const valores = brutos.map((_, i) => {
    const janela = brutos.slice(Math.max(0, i - 1), Math.min(brutos.length, i + 2));
    return janela.reduce((a, b) => a + b, 0) / janela.length;
  });
  const t0 = Date.now() - 3600000, t1 = Date.now();
  let min = Math.min(...valores), max = Math.max(...valores);
  // faixa mínima de ±5% da média (ou ±1): variação pequena aparece quase reta, como no Grafana com escala fixa
  const centro = (min + max) / 2, meia = Math.max((max - min) / 2, Math.abs(centro) * 0.05, 1);
  min = centro - meia; max = centro + meia;
  const x = (t: number) => Math.max(0, Math.min(L, ((t - t0) / (t1 - t0)) * L));
  const y = (v: number) => A - 3 - ((v - min) / (max - min)) * (A - 8);
  // curva suave (Catmull-Rom → Bézier), como a interpolação "smooth" do Grafana; controles presos à faixa do gráfico
  const pts = serie.map((p, i) => [x(p.t), y(valores[i])] as const);
  const preso = (v: number) => Math.max(2, Math.min(A - 1, v));
  const linha = pts.map(([px, py], i) => {
    if (i === 0) return `M${px.toFixed(1)},${py.toFixed(1)}`;
    const [x0, y0] = pts[Math.max(0, i - 2)], [x1, y1] = pts[i - 1], [x3, y3] = pts[Math.min(pts.length - 1, i + 1)];
    const c1 = [x1 + (px - x0) / 6, preso(y1 + (py - y0) / 6)], c2 = [px - (x3 - x1) / 6, preso(py - (y3 - y1) / 6)];
    return `C${c1[0].toFixed(1)},${c1[1].toFixed(1)} ${c2[0].toFixed(1)},${c2[1].toFixed(1)} ${px.toFixed(1)},${py.toFixed(1)}`;
  }).join(" ");
  const area = `${linha} L${x(serie[serie.length - 1].t).toFixed(1)},${A} L${x(serie[0].t).toFixed(1)},${A} Z`;
  const casas = MEDIDAS[medida]?.casas ?? 1;
  const f = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: casas });
  return (
    <svg className={`grafico-hora${anormal ? " anormal" : ""}`} viewBox={`0 0 ${L} ${A}`} preserveAspectRatio="none" role="img"
      aria-label={`última hora: mín ${f(Math.min(...valores))}, máx ${f(Math.max(...valores))}`}>
      <title>{`última hora · mín ${f(Math.min(...valores))} · máx ${f(Math.max(...valores))} ${unidadeMedida(medida)}`}</title>
      <defs>
        <linearGradient id={`g${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" className="topo" />
          <stop offset="100%" className="base" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#g${id})`} />
      <path d={linha} className="linha" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function avaliacao(device: string, medida: string, bruto: number, nominal?: number, tensao?: number) {
  return MEDIDAS[medida]?.avaliar?.(valorDe(medida, bruto), { correnteNominal: nominal, tensaoNominal: tensao }) ?? null;
}

export function Valor({ device, medida, grande = false }: { device: string; medida: string; grande?: boolean }) {
  const { empresa, vivo, ativosPorDevice } = useCco();
  const serie = useHistoricoHora(empresa, device, medida);
  const leitura = vivo.leituras.get(chave(device, medida));
  const velho = vivo.velhos.has(device);
  if (!leitura) return <div className="valor-vivo sem-dado"><span className="nx-suave">{nomeMedida(medida)}</span><b>—</b></div>;
  const ativos = ativosPorDevice.get(device);
  const anormal = avaliacao(device, medida, leitura.valor, correnteNominal(ativos), tensaoNominal(ativos, leitura.valor));
  // a última leitura ao vivo fecha a linha no "agora"
  const comAgora = serie.length ? [...serie, { t: Date.parse(leitura.timestamp) || Date.now(), v: leitura.valor }] : serie;
  return (
    <div className={`valor-vivo${anormal ? ` prioridade-${anormal.prioridade}` : ""}${velho ? " velho" : ""}${grande ? " grande" : ""}`}
      title={anormal ? `${NOME_PRIORIDADE[anormal.prioridade]}: ${anormal.motivo}` : MEDIDAS[medida]?.faixa ? `faixa normal ${MEDIDAS[medida].faixa}` : undefined}>
      <span className="nx-suave rotulo-valor">{nomeMedida(medida)}</span>
      <span className="linha-valor">
        {anormal && <span className="forma" aria-label={`prioridade ${NOME_PRIORIDADE[anormal.prioridade]}`}>{FORMA[anormal.prioridade]}</span>}
        <b className="nx-valor">{fmtValor(medida, leitura.valor)}</b>
        <small className="nx-suave">{unidadeMedida(medida)}</small>
      </span>
      {anormal && <small className="motivo">{anormal.motivo}</small>}
      {velho && <small className="selo-velho">dado velho</small>}
      <GraficoHora serie={comAgora} medida={medida} anormal={Boolean(anormal)} />
    </div>
  );
}
